import React from 'react';
import { Route } from 'react-router-dom';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList
} from '../../../components/Breadcrumb';
import { ButtonLink } from '../../../components/Button';
import { Column, Row } from '../../../components/Grid';
import { Anker, TextLink } from '../../../components/Link';
import { Section } from '../../../components/Section';
import { SvgIcon } from '../../../components/SvgIcon';
import FetchVehicleDetails from '../../../provider/SearchProvider/FetchVehicleDetails';
import {
  withSearch,
  WithSearchProps
} from '../../../provider/SearchProvider/withSearch';
import {
  PAGE_COUNTRIES,
  PAGE_DETAIL,
  PAGE_MANUFACTURERS
} from '../../../provider/Typo3Provider/config/t3PageMapping';
import {
  selectMenu,
  selectMeta,
  selectSearchDetailVehicleId
} from '../../../provider/Typo3Provider/selectors';
import { MenuEntry } from '../../../provider/Typo3Provider/types';
import Typo3Label from '../../../provider/Typo3Provider/Typo3Label';
import {
  withTypo3,
  WithTypo3Props
} from '../../../provider/Typo3Provider/withTypo3';
import { ThemeColors } from '../../../theme';
import SearchDetailNavigation from '../../SearchContainer/SearchDetail/SearchDetailNavigation';

interface BreadcrumbContainerProps {
  bg?: string;
}

interface BreadcrumbContentProps {
  uid: number;
  item: MenuEntry;
}

const BreadcrumbContent = withTypo3(({
  item: { link, title },
  uid,
  typo3
}: BreadcrumbContentProps & WithTypo3Props) => {
  const defaultLink = <TextLink to={link}>{title}</TextLink>;
  switch (uid) {
    case PAGE_DETAIL:
      return (
        <Route
          render={({ location }) => {
            const vehicleId = selectSearchDetailVehicleId(typo3);
            return vehicleId ? (
              <FetchVehicleDetails
                // we want a new instance of `FetchVehicleDetails`
                // each time `vehicleId` changes
                key={vehicleId}
                vehicleId={vehicleId}
                render={({ loading, error, vehicleDetails }) =>
                  !loading && !error && vehicleDetails ? (
                    <TextLink to={location}>
                      {vehicleDetails.manufacturer} {vehicleDetails.model}
                    </TextLink>
                  ) : (
                    defaultLink
                  )
                }
              />
            ) : (
              defaultLink
            );
          }}
        />
      );
    case PAGE_COUNTRIES:
    case PAGE_MANUFACTURERS:
      // https://css-tricks.com/how-to-disable-links/
      return <Anker>{title}</Anker>;
    default:
      return <TextLink to={link}>{title}</TextLink>;
  }
});

const renderBreadcrumbEntry = (
  item: MenuEntry,
  index: number,
  breadcrumbEntries: MenuEntry[]
) => {
  const { uid } = item;
  return (
    <BreadcrumbItem key={uid}>
      <BreadcrumbContent uid={uid} item={item} />
      {index < breadcrumbEntries.length - 1 && <SvgIcon icon="arrow_full" />}
    </BreadcrumbItem>
  );
};

const BreadcrumbContainer = ({
  typo3,
  search,
  bg
}: BreadcrumbContainerProps & WithTypo3Props & WithSearchProps) => {
  const breadcrumb = selectMenu(typo3, 'breadcrumb');
  const [magazineOverview = null] = selectMenu(typo3, 'magazineoverview');
  const meta = selectMeta(typo3);
  if (meta) {
    // typescript hack
    const background: keyof ThemeColors = bg as any;

    return (
      <Breadcrumb bg={background} className="hide-for-print">
        <Section noPadding>
          <Row noGap>
            <Column small={12} medium={12} large={6} alignSelf="center">
              <BreadcrumbList>
                {breadcrumb.map(renderBreadcrumbEntry)}
              </BreadcrumbList>
            </Column>

            {meta.page.doktype === 52 && magazineOverview && (
              <Column small={12} medium={12} large={6}>
                <ButtonLink small inlined to={magazineOverview.link} magazine>
                  <Typo3Label label="searchDetail.navigation.overview" />
                </ButtonLink>
              </Column>
            )}

            {meta.page.uid === PAGE_DETAIL && search.searchResult && (
              <Column small={12} medium={12} large={6}>
                <SearchDetailNavigation />
              </Column>
            )}
          </Row>
        </Section>
      </Breadcrumb>
    );
  }
  return null;
};

export default withSearch(withTypo3(BreadcrumbContainer));
